"use client";

import { useCallback, useEffect, useState, type FormEvent } from "react";
import { X } from "lucide-react";
import { unwrapApiData } from "@/lib/apiResponse";
import { fetchWithAuth } from "@/lib/fetchWithAuth";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
const SUS_SURVEY_DELAY_MS = 1_500;

export const SUS_QUESTIONS = [
  "나는 이 AI 시스템을 자주 사용하고 싶다.",
  "이 시스템은 불필요하게 복잡하다고 느꼈다.",
  "이 시스템은 사용하기 쉬웠다.",
  "이 시스템을 사용하려면 기술 지원 인력의 도움이 필요할 것 같다.",
  "이 시스템의 여러 기능이 잘 통합되어 있다고 느꼈다.",
  "이 시스템에는 일관성이 부족한 부분이 너무 많다고 느꼈다.",
  "대부분의 사람이 이 시스템 사용법을 매우 빠르게 익힐 것이라고 생각한다.",
  "이 시스템은 사용하기에 매우 번거로웠다.",
  "나는 이 시스템을 사용하는 데 자신감을 느꼈다.",
  "이 시스템을 제대로 사용하기 전에 많은 것을 배워야 했다.",
] as const;

const SUS_OPTIONS = [
  { value: 1, label: "전혀 그렇지 않다" },
  { value: 2, label: "그렇지 않다" },
  { value: 3, label: "보통이다" },
  { value: 4, label: "그렇다" },
  { value: 5, label: "매우 그렇다" },
] as const;

type SurveyStatus = {
  completed: boolean;
  completedAt: string | null;
  surveyVersion: string;
  accountKey: string;
};

type Answers = Array<number | null>;

type LoadStatus = "idle" | "loading" | "ready" | "error";

function emptyAnswers(): Answers {
  return Array<number | null>(SUS_QUESTIONS.length).fill(null);
}

function dismissKey(status: SurveyStatus) {
  return `seexr:sus-survey:top-dismissed:${status.surveyVersion}:${status.accountKey}`;
}

function useSusSurvey() {
  const [loadStatus, setLoadStatus] = useState<LoadStatus>("idle");
  const [status, setStatus] = useState<SurveyStatus | null>(null);
  const [answers, setAnswers] = useState<Answers>(emptyAnswers);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dismissedForSession, setDismissedForSession] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoadStatus("loading");

    const load = async () => {
      try {
        const response = await fetchWithAuth(`${API_URL}/api/sus-survey/status`, {
          skipRedirectOn401: true,
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`SUS status request failed: ${response.status}`);
        }
        const data = unwrapApiData<SurveyStatus>(await response.json());
        if (!data?.accountKey) throw new Error("Invalid SUS status response");
        setStatus(data);
        setDismissedForSession(
          window.sessionStorage.getItem(dismissKey(data)) === "1",
        );
        setLoadStatus("ready");
      } catch (error) {
        if (controller.signal.aborted) return;
        console.warn("[SusSurvey] status load failed:", error);
        setLoadStatus("error");
      }
    };

    void load();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (
      loadStatus !== "ready" ||
      !status ||
      status.completed ||
      dismissedForSession ||
      isOpen
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      setAnswers(emptyAnswers());
      setSaveError(null);
      setIsOpen(true);
    }, SUS_SURVEY_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [dismissedForSession, isOpen, loadStatus, status]);

  const close = useCallback(() => {
    if (isSaving) return;
    if (status) {
      window.sessionStorage.setItem(dismissKey(status), "1");
      setDismissedForSession(true);
    }
    setIsOpen(false);
    setSaveError(null);
  }, [isSaving, status]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [close, isOpen]);

  const updateAnswer = useCallback((questionIndex: number, value: number) => {
    setAnswers((previous) => {
      const next = [...previous];
      next[questionIndex] = value;
      return next;
    });
    setSaveError(null);
  }, []);

  const save = useCallback(async () => {
    if (
      !status ||
      status.completed ||
      isSaving ||
      answers.some((value) => value === null)
    ) {
      return false;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const payload = Object.fromEntries(
        answers.map((value, index) => [`q${index + 1}`, value]),
      );
      const response = await fetchWithAuth(`${API_URL}/api/sus-survey/response`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`SUS submit failed: ${response.status}`);
      }
      const result = unwrapApiData<SurveyStatus>(await response.json());
      if (!result?.completed) throw new Error("Invalid SUS submit response");

      window.sessionStorage.removeItem(dismissKey(status));
      setStatus(result);
      setIsOpen(false);
      return true;
    } catch (error) {
      console.error("[SusSurvey] submit failed:", error);
      setSaveError("응답을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.");
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [answers, isSaving, status]);

  return {
    answers,
    isOpen,
    isSaving,
    saveError,
    updateAnswer,
    close,
    save,
  };
}

export function SusSurveyGlobalPrompt() {
  const survey = useSusSurvey();
  const [currentStep, setCurrentStep] = useState(0);

  if (!survey.isOpen) return null;

  const currentQuestion = SUS_QUESTIONS[currentStep];
  const selectedValue = survey.answers[currentStep];
  const isLastStep = currentStep === SUS_QUESTIONS.length - 1;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (selectedValue === null) return;
    if (!isLastStep) {
      setCurrentStep((previous) => previous + 1);
      return;
    }
    await survey.save();
  };

  return (
    <div
      className="pointer-events-none"
      style={{
        position: "fixed",
        top: 10,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 11500,
        width: "min(540px, calc(100vw - 20px))",
      }}
    >
      <div className="relative" style={{ width: "100%", maxWidth: 540 }}>
        <div
          aria-hidden="true"
          className="absolute pointer-events-none"
          style={{
            left: -64,
            right: -64,
            top: -54,
            bottom: -54,
            borderRadius: 44,
            background:
              "radial-gradient(ellipse at center, rgba(59,130,246,0.14) 0%, rgba(59,130,246,0.07) 38%, rgba(148,163,184,0.04) 62%, transparent 80%)",
            filter: "blur(22px)",
            opacity: 0.9,
          }}
        />
        <div
          aria-hidden="true"
          className="absolute pointer-events-none"
          style={{
            left: "18%",
            right: "18%",
            bottom: -14,
            height: 26,
            borderRadius: "50%",
            background: "rgba(15,23,42,0.10)",
            filter: "blur(18px)",
          }}
        />
        <section
          role="dialog"
          aria-labelledby="sus-survey-title"
          aria-describedby="sus-survey-description"
          className="pointer-events-auto relative overflow-hidden rounded-[16px] border border-black/10 bg-white/95 shadow-[0_10px_34px_rgba(15,23,42,0.18),0_0_22px_rgba(59,130,246,0.10),0_2px_8px_rgba(15,23,42,0.10)] backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-200 dark:border-white/10 dark:bg-[#19191c]/95 dark:shadow-[0_12px_38px_rgba(0,0,0,0.48),0_0_24px_rgba(96,165,250,0.12),0_2px_10px_rgba(0,0,0,0.28)]"
          style={{ width: "100%", maxWidth: 540, boxSizing: "border-box" }}
        >
        <div className="relative px-4 pb-3 pt-4">
          <button
            type="button"
            onClick={survey.close}
            disabled={survey.isSaving}
            aria-label="SUS 설문 닫기"
            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 dark:hover:bg-white/10 dark:hover:text-gray-200"
          >
            <X size={15} aria-hidden="true" />
          </button>

          <div className="pr-12">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <strong
                id="sus-survey-title"
                className="text-[14px] font-black text-gray-950 dark:text-gray-100"
              >
                시스템 사용성 설문
              </strong>
              <span className="rounded-full bg-seexr-50 px-2.5 py-0.5 text-[10px] font-bold text-seexr-primary dark:bg-seexr-950/40">
                소요시간 약 1분
              </span>
            </div>
            <p
              id="sus-survey-description"
              className="mt-1.5 text-[11px] font-medium leading-[1.5] text-gray-500 dark:text-gray-400"
            >
              10문항만 답해주시면 됩니다. 한 번 제출하면 이 계정에는 다시 표시되지 않습니다.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-4">
            <div className="flex items-center justify-between gap-3">
              <span className="shrink-0 text-[10px] font-black text-seexr-primary">
                {currentStep + 1}/{SUS_QUESTIONS.length}
              </span>
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-white/[0.08]">
                <div
                  className="h-full rounded-full bg-seexr-solid transition-[width] duration-200"
                  style={{
                    width: `${((currentStep + 1) / SUS_QUESTIONS.length) * 100}%`,
                  }}
                />
              </div>
            </div>

            <p className="mt-3 text-[14px] font-bold leading-[1.45] text-gray-900 dark:text-gray-100">
              {currentQuestion}
            </p>

            <div
              className="mt-2.5 grid grid-cols-5 gap-1"
              style={{
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                width: "100%",
              }}
            >
              {SUS_OPTIONS.map((option) => {
                const checked = selectedValue === option.value;
                return (
                  <label
                    key={option.value}
                    className={`relative flex h-[44px] cursor-pointer flex-col items-center justify-center rounded-lg border text-center transition-colors focus-within:ring-2 focus-within:ring-seexr-solid/30 ${checked ? "border-seexr-solid bg-seexr-solid text-white" : "border-gray-200 bg-gray-50 text-gray-500 hover:border-seexr-solid/40 hover:bg-seexr-solid/5 dark:border-[#39393f] dark:bg-[#222225] dark:text-gray-300"}`}
                  >
                    <input
                      type="radio"
                      name={`sus-question-${currentStep + 1}`}
                      value={option.value}
                      checked={checked}
                      onChange={() =>
                        survey.updateAnswer(currentStep, option.value)
                      }
                      aria-label={`${currentQuestion} — ${option.label} (${option.value})`}
                      className="sr-only"
                    />
                    <span className="text-[13px] font-black">{option.value}</span>
                    <span
                      className={`mt-0.5 text-[8px] font-semibold leading-none ${checked ? "text-white/80" : "text-gray-400"}`}
                    >
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>

            {survey.saveError && (
              <p
                role="alert"
                className="mt-2 text-[9px] font-semibold text-red-600 dark:text-red-300"
              >
                {survey.saveError}
              </p>
            )}

            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                type="button"
                disabled={currentStep === 0 || survey.isSaving}
                onClick={() =>
                  setCurrentStep((previous) => Math.max(0, previous - 1))
                }
                className="h-8 rounded-lg px-3 text-[10px] font-bold text-gray-500 transition-colors hover:bg-gray-100 disabled:invisible dark:text-gray-300 dark:hover:bg-white/10"
              >
                이전
              </button>
              <span className="text-[9px] font-medium text-gray-400">
                이름·전화번호 입력 없음
              </span>
              <button
                type="submit"
                disabled={selectedValue === null || survey.isSaving}
                className="inline-flex h-8 min-w-16 items-center justify-center gap-1 rounded-lg bg-seexr-solid px-3 text-[10px] font-black text-white transition-colors hover:bg-seexr-hover disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-[#2a2a2e] dark:disabled:text-gray-500"
              >
                {survey.isSaving ? "저장 중…" : isLastStep ? "제출" : "다음"}
              </button>
            </div>
          </form>
        </div>
        </section>
      </div>
    </div>
  );
}
